<template>
  <div class="cereri-page">
    <div class="cereri-inner">
      <!-- Hero -->
      <div class="hero">
        <div class="hero-txt">
          <div class="hero-eyebrow">PORTAL HR</div>
          <h1 class="hero-title">Cererile mele de concediu</h1>
          <p class="hero-sub">Istoricul cererilor tale, statusul și cine trebuie să le aprobe.</p>
        </div>
        <div class="hero-actions">
          <div class="co-chip">
            <v-icon size="18" class="mr-1">mdi-umbrella-beach</v-icon>
            <span class="co-val">{{ zileCO }}</span>
            <span class="co-lbl">zile CO {{ anCurent }}</span>
          </div>
          <v-btn class="btn-plan" variant="outlined" prepend-icon="mdi-calendar-month" @click="$router.push({ name: 'PortalPlanificareAnuala' })">
            Planificare concediu
          </v-btn>
          <v-btn class="btn-nou" prepend-icon="mdi-plus" @click="$router.push({ name: 'PortalCerereNoua' })">
            Cerere nouă
          </v-btn>
        </div>
      </div>

      <!-- Sumar stări -->
      <div class="sumar">
        <div v-for="s in sumar" :key="s.key" class="sumar-card" :style="{ '--c': s.color }">
          <v-icon :color="s.color" size="22" class="sumar-ic">{{ s.icon }}</v-icon>
          <div>
            <div class="sumar-val">{{ s.count }}</div>
            <div class="sumar-lbl">{{ s.label }}</div>
          </div>
        </div>
      </div>

      <!-- Grid -->
      <div class="grid-card">
        <div class="grid-head">
          <v-icon size="18" color="#ff5722" class="mr-2">mdi-format-list-bulleted</v-icon>
          <span>Toate cererile</span>
          <v-spacer />
          <span class="grid-hint">Click pe o cerere pentru fluxul de aprobare</span>
        </div>
        <DxDataGrid
          :data-source="cereri"
          key-expr="ID"
          :show-borders="false"
          :show-column-lines="false"
          :show-row-lines="true"
          :row-alternation-enabled="false"
          :hover-state-enabled="true"
          :no-data-text="loading ? 'Se încarcă…' : 'Nu ai depus nicio cerere încă.'"
        >
          <DxColumn data-field="DataInceput" caption="Perioadă" data-type="date" :width="220" cell-template="perioadaCell" :allow-filtering="false" />
          <DxColumn data-field="NumarZile" caption="Zile" :width="80" alignment="center" cell-template="zileCell" />
          <DxColumn data-field="TipConcediu" caption="Tip concediu" :min-width="150" />
          <DxColumn data-field="Descriere" caption="Observații" :min-width="180" />
          <DxColumn data-field="Stare" caption="Stare" :width="240" cell-template="stareCell" />
          <DxColumn caption="" :width="56" :allow-filtering="false" :allow-sorting="false" cell-template="actiuniCell" />

          <DxMasterDetail :enabled="true" template="fluxTemplate" />

          <template #perioadaCell="{ data }">
            <div class="perioada">
              <v-icon size="16" color="#ff5722" class="mr-2">mdi-calendar-range</v-icon>
              <span>{{ fmt(data.data.DataInceput) }} <span class="arr">→</span> {{ fmt(data.data.DataSfarsit) }}</span>
            </div>
          </template>

          <template #zileCell="{ data }">
            <span class="zile-badge">{{ data.data.NumarZile }}</span>
          </template>

          <template #stareCell="{ data }">
            <div class="stare-wrap">
              <span class="stare-pill" :style="starePillStyle(data.data.Stare)">
                <span class="dot" :style="{ background: stareColor(data.data.Stare) }" />
                {{ stareLabel(data.data.Stare) }}
              </span>
              <span v-if="data.data.AsteaptaAprobareDeLa" class="asteapta-la">
                <v-icon size="12">mdi-arrow-right-thin</v-icon>{{ data.data.AsteaptaAprobareDeLa }}
              </span>
            </div>
          </template>

          <template #actiuniCell="{ data }">
            <v-btn
              v-if="['0', '-3'].includes(data.data.Stare)"
              icon="mdi-close-circle-outline"
              size="small"
              color="red"
              variant="text"
              @click="anuleaza(data.data.ID)"
              title="Anulează cererea"
            />
          </template>

          <!-- Flux aprobare (component standalone — izolează ciclul reactiv de template-ul DevExtreme) -->
          <template #fluxTemplate="{ data }">
            <FluxAprobare :id="data.data.ID" />
          </template>

          <DxPaging :page-size="12" />
          <DxSorting mode="single" />
          <DxFilterRow :visible="true" />
        </DxDataGrid>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { DxDataGrid, DxColumn, DxPaging, DxFilterRow, DxSorting, DxMasterDetail } from 'devextreme-vue/data-grid'
import DataSource from 'devextreme/data/data_source'
import PORTAL_CereriConcedii from '@/services/Portal/PORTAL_CereriConcedii'
import FluxAprobare from '@/views/Portal/components/FluxAprobare.vue'

export default defineComponent({
  name: 'CereriConcediu',
  components: { DxDataGrid, DxColumn, DxPaging, DxFilterRow, DxSorting, DxMasterDetail, FluxAprobare },
  data() {
    const now = new Date();
    return {
      anCurent: now.getFullYear(),
      zileCO: 0,
      loading: true,
      cereri: [] as any[],
    }
  },
  computed: {
    sumar(): any[] {
      const c = this.cereri;
      const by = (fn: (x: any) => boolean) => c.filter(fn).length;
      return [
        { key: 'total', label: 'Total cereri', color: '#ff5722', icon: 'mdi-file-document-multiple-outline', count: c.length },
        { key: 'wait', label: 'În așteptare', color: '#f59e0b', icon: 'mdi-clock-outline', count: by(x => x.Stare === '0' || x.Stare === '-3') },
        { key: 'ok', label: 'Aprobate', color: '#16a34a', icon: 'mdi-check-circle-outline', count: by(x => x.Stare === '1') },
        { key: 'no', label: 'Respinse / anulate', color: '#dc2626', icon: 'mdi-close-circle-outline', count: by(x => x.Stare === '-1' || x.Stare === '-2') },
      ];
    }
  },
  async mounted() {
    await Promise.all([this.incarcaCereri(), this.incarcaSold()]);
  },
  methods: {
    fmt(d: any) {
      if (!d) return '—';
      return new Date(d).toLocaleDateString('ro-RO', { day: '2-digit', month: '2-digit', year: 'numeric' });
    },
    async incarcaSold() {
      try {
        const resp = await PORTAL_CereriConcedii.getFormInit();
        this.zileCO = resp.data?.SoldCurent ?? 0;
      } catch { /* ignore */ }
    },
    async incarcaCereri() {
      this.loading = true;
      try {
        const ds = new DataSource({ store: PORTAL_CereriConcedii.getODataStore(), sort: [{ selector: 'DataInceput', desc: true }] });
        const [items, aprobatori] = await Promise.all([
          ds.load(),
          this.incarcaAprobatori(),
        ]);
        const lista = Array.isArray(items) ? items : (items as any)?.data ?? [];
        // Precalculăm numele aprobatorului curent ca proprietate statică pe fiecare rând
        // (fără apeluri de funcție în template — evită „Maximum recursive updates" din DevExtreme).
        this.cereri = lista.map((c: any) => ({
          ...c,
          AsteaptaAprobareDeLa: (c.Stare === '0' || c.Stare === '-3') ? (aprobatori[c.ID] ?? '') : '',
        }));
      } catch {
        this.cereri = [];
      } finally {
        this.loading = false;
      }
    },
    async incarcaAprobatori(): Promise<Record<string, string>> {
      try {
        const resp = await PORTAL_CereriConcedii.getAprobatoriCurenti();
        return resp.data ?? {};
      } catch {
        return {};
      }
    },
    stareColor(stare: string) {
      const map: Record<string, string> = { '0': '#f59e0b', '-3': '#f97316', '1': '#16a34a', '-1': '#dc2626', '-2': '#94a3b8' };
      return map[stare] ?? '#94a3b8';
    },
    starePillStyle(stare: string) {
      const soft: Record<string, string> = { '0': '#fff7ed', '-3': '#fff1ec', '1': '#ecfdf5', '-1': '#fef2f2', '-2': '#f1f5f9' };
      return { background: soft[stare] ?? '#f1f5f9', color: this.stareColor(stare) };
    },
    stareLabel(stare: string) {
      const map: Record<string, string> = { '0': 'În așteptare', '-3': 'Așteaptă înlocuitor', '1': 'Aprobată', '-1': 'Respinsă', '-2': 'Anulată' };
      return map[stare] ?? stare;
    },
    async anuleaza(id: string) {
      await PORTAL_CereriConcedii.anuleaza(id);
      await this.incarcaCereri();
    },
  }
})
</script>

<style scoped>
.cereri-page { padding: 16px; width: 100%; }
.cereri-inner { width: 100%; }

/* Hero */
.hero {
  display: flex; align-items: center; justify-content: space-between; gap: 16px; flex-wrap: wrap;
  background: linear-gradient(120deg, #ff5722 0%, #ff7a45 55%, #ff9a5c 100%);
  border-radius: 18px; padding: 24px 28px; color: #fff;
  box-shadow: 0 10px 30px rgba(255, 87, 34, .28); margin-bottom: 18px;
}
.hero-eyebrow { font-size: 10px; font-weight: 700; letter-spacing: 2px; opacity: .85; }
.hero-title { font-size: 23px; font-weight: 800; line-height: 1.2; margin: 4px 0 2px; }
.hero-sub { font-size: 13px; opacity: .92; margin: 0; }
.hero-actions { display: flex; align-items: center; gap: 12px; }
.co-chip {
  display: flex; align-items: center; gap: 5px; background: rgba(255, 255, 255, .22);
  padding: 9px 15px; border-radius: 22px;
}
.co-val { font-size: 18px; font-weight: 800; }
.co-lbl { font-size: 11px; font-weight: 600; opacity: .92; }
.btn-nou { background: #fff !important; color: #ff5722 !important; font-weight: 700; border-radius: 22px; text-transform: none; letter-spacing: 0; box-shadow: 0 4px 14px rgba(0,0,0,.12); }
.btn-plan { color: #fff !important; border-color: rgba(255,255,255,.6) !important; font-weight: 700; border-radius: 22px; text-transform: none; letter-spacing: 0; }

/* Sumar */
.sumar { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; margin-bottom: 18px; }
@media (max-width: 720px) { .sumar { grid-template-columns: repeat(2, 1fr); } }
.sumar-card {
  display: flex; align-items: center; gap: 14px; background: #fff; border: 1px solid #eef1f6;
  border-radius: 16px; padding: 18px 20px; box-shadow: 0 2px 6px rgba(15, 23, 42, .06);
  transition: transform .18s ease, box-shadow .18s ease;
}
.sumar-card:hover { transform: translateY(-3px); box-shadow: 0 10px 26px rgba(15, 23, 42, .1); }
.sumar-ic { background: color-mix(in srgb, var(--c) 12%, #fff); border-radius: 12px; padding: 9px; box-sizing: content-box; }
.sumar-val { font-size: 27px; font-weight: 800; color: var(--c); line-height: 1; }
.sumar-lbl { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .5px; color: #94a3b8; margin-top: 6px; }

/* Grid card */
.grid-card { background: #fff; border: 1px solid #eef1f6; border-radius: 18px; box-shadow: 0 2px 8px rgba(15, 23, 42, .06); overflow: hidden; }
.grid-head { display: flex; align-items: center; padding: 18px 22px; border-bottom: 1px solid #f1f5f9; font-size: 14px; font-weight: 700; color: #1e293b; background: #fcfcfd; }
.grid-hint { font-size: 11px; font-weight: 600; color: #94a3b8; }
.perioada { display: flex; align-items: center; font-weight: 600; color: #1e293b; }
.perioada .arr { color: #ff5722; margin: 0 4px; font-weight: 700; }
.zile-badge {
  display: inline-block; min-width: 30px; padding: 4px 10px; border-radius: 10px;
  background: #fff1ec; color: #ff5722; font-weight: 800; font-size: 13px; text-align: center;
}
.stare-wrap { display: flex; flex-direction: column; align-items: flex-start; gap: 4px; }
.stare-pill { display: inline-flex; align-items: center; gap: 7px; padding: 6px 14px; border-radius: 20px; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .5px; box-shadow: 0 1px 2px rgba(0,0,0,.04); }
.stare-pill .dot { width: 7px; height: 7px; border-radius: 50%; display: inline-block; }
.asteapta-la { display: inline-flex; align-items: center; gap: 1px; font-size: 11px; font-weight: 600; color: #94a3b8; padding-left: 4px; }
.asteapta-la .v-icon { color: #ff5722; }
</style>
